import {
  createPublicNavItems,
  defaultSocialLinks,
  GlassButton,
  GlassCard,
  GlassPanel,
  SectionHeader,
  SiteNav,
  VisualShell
} from "@jinze-ai/ui";
import {
  Bot,
  Camera,
  PenLine,
  Search,
  Sparkles,
  type LucideIcon
} from "lucide-react";

type Destination = {
  title: string;
  href: string;
  label: string;
  description: string;
  signal: string;
};

function underConstructionHref(app: string) {
  return `/under-construction?app=${encodeURIComponent(app)}`;
}

const primaryNav = createPublicNavItems({ underConstructionHref });

const destinations: Destination[] = [
  {
    title: "Me",
    href: "https://me.jinze.ai",
    label: "Identity",
    description:
      "A professional profile for selected work, research notes, project history, and current engineering focus.",
    signal: "profile"
  },
  {
    title: "Blog",
    href: underConstructionHref("blog"),
    label: "Writing",
    description:
      "Bilingual essays on software, AI systems, field notes, and the texture of everyday technical life.",
    signal: "essays"
  },
  {
    title: "Photo",
    href: underConstructionHref("photo"),
    label: "Photography",
    description:
      "A quiet gallery for light, travel, memory, and visual field notes from places worth keeping.",
    signal: "gallery"
  },
  {
    title: "Links",
    href: underConstructionHref("links"),
    label: "Index",
    description:
      "A compact index of current links, reading, tools, references, and small things worth returning to.",
    signal: "index"
  }
];

type FocusItem = {
  label: string;
  icon: LucideIcon;
};

const currentFocus: FocusItem[] = [
  { label: "Autonomous agents", icon: Bot },
  { label: "Company-Wide Agent System", icon: Search },
  { label: "Editorial systems", icon: PenLine },
  { label: "Photography notes", icon: Camera }
];

export function App() {
  const isUnderConstruction = window.location.pathname !== "/";

  if (isUnderConstruction) {
    return <UnderConstructionPage />;
  }

  return (
    <VisualShell>
      <SiteNav
        activeLabel="Home"
        brand={{
          label: "jinze.ai",
          href: "https://jinze.ai",
          ariaLabel: "jinze.ai home"
        }}
        items={primaryNav}
      />

      <main>
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-copy">
            <p className="hello">Hello.</p>
            <h1 id="hero-title">Building systems, stories, and tools.</h1>
            <p className="role">
              Software engineer, builder, writer, and photographer.
            </p>
            <p className="intro">
              A quiet space for engineering, writing, photography, and
              long-running experiments.
            </p>
            <div className="hero-actions" aria-label="Featured links">
              <GlassButton href="#ecosystem">Explore the ecosystem</GlassButton>
              <GlassButton href={underConstructionHref("blog")}>Read the blog</GlassButton>
            </div>
          </div>
        </section>

        <GlassPanel
          className="ecosystem-panel section-reveal"
          id="ecosystem"
          aria-labelledby="ecosystem-title"
        >
          <div className="ecosystem-head">
            <SectionHeader
              id="ecosystem-title"
              label="Ecosystem"
              title="A small map of public rooms."
              description="Identity, writing, photography, and references orbit the same quiet working space."
            />
            <div className="map-orbit" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          </div>
          <div className="destination-map">
            {destinations.map((destination) => (
              <GlassCard
                className="destination-card"
                href={destination.href}
                key={destination.href}
              >
                <div>
                  <span className="card-label">{destination.label}</span>
                  <strong>{destination.title}</strong>
                </div>
                <span>{destination.description}</span>
                <em>{destination.signal}</em>
              </GlassCard>
            ))}
          </div>
        </GlassPanel>

        <section className="focus-section section-reveal" aria-labelledby="focus-title">
          <SectionHeader
            id="focus-title"
            label="Now"
            title="Current focus"
            description="A few threads I am paying attention to while the ecosystem grows one room at a time."
          />
          <div className="focus-grid">
            {currentFocus.map(({ icon: Icon, label }) => (
              <GlassCard className="focus-card" key={label}>
                <span className="focus-icon">
                  <Icon aria-hidden="true" size={18} strokeWidth={2} />
                </span>
                <span>{label}</span>
              </GlassCard>
            ))}
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <span>© 2026 Jinze.</span>
        <div className="footer-links" aria-label="Social links">
          {defaultSocialLinks.map(({ href, icon: Icon, label }) => (
            <GlassButton
              className="footer-social"
              href={href}
              key={label}
            >
              <Icon aria-hidden="true" size={16} strokeWidth={2} />
              <span>{label}</span>
            </GlassButton>
          ))}
        </div>
      </footer>
    </VisualShell>
  );
}

function UnderConstructionPage() {
  const params = new URLSearchParams(window.location.search);
  const app = params.get("app") ?? "page";
  const normalizedApp = app.replace(/[^a-z0-9-]/gi, "").toLowerCase();
  const displayName =
    normalizedApp === "blog"
      ? "Blog"
      : normalizedApp === "photo"
        ? "Photo"
        : normalizedApp === "links"
          ? "Links"
          : "This page";

  return (
    <VisualShell>
      <SiteNav
        activeLabel=""
        brand={{
          label: "jinze.ai",
          href: "https://jinze.ai",
          ariaLabel: "jinze.ai home"
        }}
        items={primaryNav}
      />

      <main className="construction-main">
        <GlassPanel className="construction-panel section-reveal">
          <span className="construction-icon" aria-hidden="true">
            <Sparkles size={24} strokeWidth={2} />
          </span>
          <p className="construction-code">404 — Under Construction</p>
          <h1>{displayName} is being shaped.</h1>
          <p>
            This room is not open yet. I am building the ecosystem one public
            space at a time, with the same quiet glass language across each
            domain.
          </p>
          <div className="construction-actions">
            <GlassButton href="https://jinze.ai">Back home</GlassButton>
            <GlassButton href="https://me.jinze.ai">Visit Me</GlassButton>
          </div>
        </GlassPanel>
      </main>
    </VisualShell>
  );
}
